/**
 * api 薄包。handle auth header + JSON + error shape。
 */

export interface MeProfile {
  birthDate: string | null;
  birthTime: string | null;
  timezone: string | null;
  gender: 'male' | 'female' | null;
  email: string | null;
}

export interface MeResponse {
  lineUserId: string;
  phase: string;
  profile: MeProfile;
  natalChart: unknown;
}

export interface PatchMePayload {
  birthDate?: string;
  birthTime?: string;
  timezone?: string;
  gender?: 'male' | 'female';
  email?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function baseUrl(): string {
  const v = import.meta.env.VITE_API_BASE_URL;
  if (!v) throw new Error('VITE_API_BASE_URL not set');
  return v.replace(/\/$/, '');
}

async function jsonFetch<T>(
  path: string,
  init: RequestInit & { idToken: string | null },
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (init.idToken) {
    headers.set('authorization', `Bearer ${init.idToken}`);
  }

  const res = await fetch(`${baseUrl()}${path}`, {
    ...init,
    headers,
  });

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const message =
      (body as { error?: string } | null)?.error ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }

  return body as T;
}

export function getMe(idToken: string | null): Promise<MeResponse> {
  return jsonFetch<MeResponse>('/me', { method: 'GET', idToken });
}

export function patchMe(
  idToken: string | null,
  payload: PatchMePayload,
): Promise<MeResponse> {
  return jsonFetch<MeResponse>('/me', {
    method: 'PATCH',
    idToken,
    body: JSON.stringify(payload),
  });
}
